import React from "react";
import { Plus, Trash2 } from "lucide-react";

/**
 * Theme-aware, minimal in-cell controls (primary focus ring matches app tokens).
 * Flush layout: cell borders carry structure.
 */
const EXPENSE_INPUT_CLASS =
  "max-w-full w-full border-0 bg-transparent px-2 py-1.5 text-xs text-text-primary outline-none ring-0 placeholder:text-text-muted focus:bg-surface focus:ring-2 focus:ring-inset focus:ring-primary/25 transition-[box-shadow,background-color]";

const stripCommas = (value = "") => String(value).replace(/,/g, "");

const formatAmountInput = (value = "") => {
  const raw = stripCommas(value).trim();
  if (!raw) return "";
  if (!/^\d*\.?\d*$/.test(raw)) return value;

  const hasTrailingDot = raw.endsWith(".");
  const [integerPart = "", decimalPart] = raw.split(".");
  const formattedInteger = (integerPart || "0").replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if (hasTrailingDot) return `${formattedInteger}.`;
  if (decimalPart !== undefined) return `${formattedInteger}.${decimalPart}`;
  return formattedInteger;
};

const EXPENSE_TEXTAREA_CLASS = `${EXPENSE_INPUT_CLASS} min-h-[2.75rem] resize-y leading-snug align-top`;

/** Default structure for table value. budgetLine = BudgetItem _id when user selects from budget options. */
export const getDefaultExpenseTableValue = (fixedDescriptions = []) => ({
  fixed: [
    {
      remarks: "",
      description: fixedDescriptions[0] ?? "",
      amount: "",
      actual: "",
      accountCode: "",
      budgetLine: "",
    },
  ],
  others: [],
});

const DISABLED_INPUT_CLASS =
  "cursor-not-allowed bg-surface-muted/90 text-text-muted";

/** Table cell: shared border; border-collapse merges shared edges (no gaps). */
const CELL = "border border-border align-top";
const TH_CELL = `${CELL} border-b-2 border-b-primary/20 bg-surface-muted px-2 py-1.5 text-left text-[11px] font-semibold tracking-wide text-text-secondary`;
const TD_CELL = `${CELL} p-0`;
const TD_SN = `${CELL} px-2 py-1.5 text-center tabular-nums text-text-muted`;

/** Budget select option label: description only (code is in the Account code column). */
const getBudgetSelectOptionLabel = (opt) => {
  if (!opt) return "";
  const desc = String(opt.accountCodeDescription ?? opt.description ?? "").trim();
  if (desc) return desc;
  return String(opt.accountCode ?? "").trim();
};

/** Tooltip / title: keep code visible on hover for disambiguation. */
const getBudgetSelectOptionTitle = (opt) => {
  if (!opt) return "";
  const code = String(opt.accountCode ?? "").trim();
  const label = getBudgetSelectOptionLabel(opt);
  if (code && label && code !== label) return `${code} — ${label}`;
  return label || code || "";
};

const parseQtyUnit = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const DEFAULT_LPO_TAX_RATE = 18;

function countExpenseRows(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return 0;
  const fx = Array.isArray(value.fixed) ? value.fixed.length : 0;
  const ot = Array.isArray(value.others) ? value.others.length : 0;
  return fx + ot;
}

/**
 * `value.fixed || value.others` is unsafe: both can be `[]`, and `[]` is truthy so
 * `value && ([] || [])` yields `[]` — then `data` becomes an array and `{ ...data }` breaks updates.
 */
function isExpenseTableValueObject(value) {
  return (
    value != null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    (Array.isArray(value.fixed) || Array.isArray(value.others))
  );
}

/**
 * VAT: unit price × qty = subtotal (excl.); line total = subtotal × (1 + tax%/100).
 * Legacy rows (amount ≈ subtotal with no tax rate stored) use 0% so totals stay unchanged.
 */
export function inferLpoTaxRatePercent(row = {}) {
  if (row.taxRate != null && String(row.taxRate).trim() !== "") {
    const r = parseQtyUnit(row.taxRate);
    return Number.isFinite(r) && r >= 0 ? r : DEFAULT_LPO_TAX_RATE;
  }
  const qty = parseQtyUnit(row.quantity) || 1;
  const up = parseQtyUnit(row.unitPrice);
  const sub = qty > 0 && up > 0 ? qty * up : 0;
  const amt = parseQtyUnit(row.amount);
  if (sub > 0 && amt > 0 && Math.abs(amt - sub) < 0.02) return 0;
  return DEFAULT_LPO_TAX_RATE;
}

export function getLpoFinancialTotalsFromItems(items) {
  if (!items || typeof items !== "object") {
    return { subtotalExcl: 0, tax: 0, totalIncl: 0 };
  }
  const fixed = Array.isArray(items.fixed) ? items.fixed : [];
  const others = Array.isArray(items.others) ? items.others : [];
  let subtotalExcl = 0;
  let tax = 0;
  let totalIncl = 0;
  for (const row of [...fixed, ...others]) {
    const qty = parseQtyUnit(row?.quantity);
    const up = parseQtyUnit(row?.unitPrice);
    const sub = qty > 0 && up > 0 ? qty * up : 0;
    const ratePct = inferLpoTaxRatePercent(row);
    if (sub > 0) {
      const lineTax = sub * (ratePct / 100);
      subtotalExcl += sub;
      tax += lineTax;
      totalIncl += sub + lineTax;
    } else {
      const lineTotal = parseQtyUnit(row?.amount);
      if (lineTotal > 0) totalIncl += lineTotal;
    }
  }
  return { subtotalExcl, tax, totalIncl };
}

const lpoLineRowShape = (row = {}) => {
  const quantity =
    row.quantity != null && String(row.quantity).trim() !== ""
      ? String(row.quantity)
      : "1";
  const unitPrice = row.unitPrice ?? "";
  const taxRatePct = inferLpoTaxRatePercent(row);
  const taxRate =
    row.taxRate != null && String(row.taxRate).trim() !== ""
      ? String(row.taxRate)
      : String(taxRatePct);
  return {
    remarks: row.remarks ?? "",
    description: row.description ?? "",
    quantity,
    unitPrice,
    taxRate,
    amount: row.amount ?? "",
    actual: row.actual ?? "",
    accountCode: row.accountCode ?? "",
    budgetLine: row.budgetLine ?? "",
  };
};

const lineTotalFromQtyPriceTax = (
  quantity,
  unitPrice,
  taxRateStr,
  fallbackAmount = "",
) => {
  const qty = parseQtyUnit(quantity);
  const up = parseQtyUnit(unitPrice);
  if (qty > 0 && up > 0) {
    let rate = parseQtyUnit(taxRateStr);
    if (!Number.isFinite(rate) || rate < 0) rate = DEFAULT_LPO_TAX_RATE;
    const sub = qty * up;
    return (sub * (1 + rate / 100)).toFixed(2);
  }
  const fb = parseQtyUnit(fallbackAmount);
  if (fb > 0) return fb.toFixed(2);
  return "";
};

export const ExpenseTable = ({
  value,
  onChange,
  fixedDescriptions = [],
  budgetOptions,
  isRetirement = false,
  isPettyCash = false,
  /** Local Purchase Order line items: Item #, Description, Qty, Unit price, Total (no budget columns). */
  isLpo = false,
  /**
   * Value shape control.
   * - "split" (default): `{ fixed: [], others: [] }`
   * - "flatItems": `{ items: [] }` (others-only behavior)
   * - "flatArray": `[]` (others-only behavior)
   */
  valueFormat = "split",
  /** Shown before the numeric total in the footer (e.g. imprest retirement). */
  currencyCode = "",
  error,
  disabled = false,
}) => {
  const [activeAmountField, setActiveAmountField] = React.useState(null);
  const [activeActualField, setActiveActualField] = React.useState(null);

  const isFlatArrayValue = Array.isArray(value);
  const isFlatItemsValue =
    value != null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Array.isArray(value.items);

  const useFlatArray = valueFormat === "flatArray" || isFlatArrayValue;
  const useFlatItems = !useFlatArray && (valueFormat === "flatItems" || isFlatItemsValue);

  const emitChange = (nextSplitValue) => {
    if (!onChange) return;
    if (useFlatArray) {
      onChange(Array.isArray(nextSplitValue?.others) ? nextSplitValue.others : []);
      return;
    }
    if (useFlatItems) {
      onChange({ items: Array.isArray(nextSplitValue?.others) ? nextSplitValue.others : [] });
      return;
    }
    onChange(nextSplitValue);
  };

  const data = (() => {
    // Flat array: treat array value as `others` (others-only table).
    if (useFlatArray) {
      const itemsFromValue = isFlatArrayValue
        ? value
        : isExpenseTableValueObject(value)
          ? value.others
          : isFlatItemsValue
            ? value.items
            : [];
      return { fixed: [], others: Array.isArray(itemsFromValue) ? itemsFromValue : [] };
    }

    // Flat mode: treat `items` as `others` (others-only table).
    if (useFlatItems) {
      const itemsFromValue = isFlatItemsValue
        ? value.items
        : isExpenseTableValueObject(value)
          ? value.others
          : [];
      return {
        fixed: [],
        others: Array.isArray(itemsFromValue) ? itemsFromValue : [],
      };
    }

    if (isExpenseTableValueObject(value) && countExpenseRows(value) > 0) {
      return value;
    }
    if (isLpo) return { fixed: [], others: [] };
    return getDefaultExpenseTableValue(
      budgetOptions?.length ? [] : isPettyCash ? [] : fixedDescriptions,
    );
  })();
  const { fixed = [], others = [] } = data;

  const descriptionOptions = fixedDescriptions.filter(Boolean);
  const getDescriptionOptions = (selectedValue = "") => {
    if (
      !selectedValue ||
      descriptionOptions.some((description) => description === selectedValue)
    ) {
      return descriptionOptions;
    }
    return [selectedValue, ...descriptionOptions];
  };

  /** Find budget option by id (BudgetItem _id) or by description / label (hydrated rows, case variants). */
  const getBudgetOption = (value) => {
    if (!Array.isArray(budgetOptions) || value == null || value === "") return null;
    const v = String(value).trim();
    if (!v) return null;
    const byId = budgetOptions.find((o) => o.id != null && String(o.id) === v);
    if (byId) return byId;
    const byDescExact = budgetOptions.find((o) => String(o.description ?? "").trim() === v);
    if (byDescExact) return byDescExact;
    const vl = v.toLowerCase();
    const byDescI = budgetOptions.find(
      (o) => String(o.description ?? "").trim().toLowerCase() === vl,
    );
    if (byDescI) return byDescI;
    const byLabel = budgetOptions.find(
      (o) => getBudgetSelectOptionLabel(o).trim().toLowerCase() === vl,
    );
    if (byLabel) return byLabel;
    return (
      budgetOptions.find((o) => String(o.accountCode ?? "").trim() === v) ?? null
    );
  };

  const getAccountCodeForBudget = (value) => {
    const opt = getBudgetOption(value);
    return opt?.accountCode ?? "";
  };

  const currencyTrim =
    currencyCode != null && String(currencyCode).trim() !== ""
      ? String(currencyCode).trim()
      : "";
  const currencyPrefix = currencyTrim ? `${currencyTrim} ` : "";
  const currencyHeaderSuffix = currencyTrim ? ` (${currencyTrim})` : "";

  const displayOthers =
    isPettyCash && others.length === 0
      ? [
          {
            remarks: "",
            description: "",
            amount: "",
            accountCode: "",
            budgetLine: "",
          },
        ]
      : others;

  const fixedRows = isPettyCash
    ? []
    : (budgetOptions?.length && fixed.length === 0
        ? []
        : fixed.length
          ? fixed
          : fixedDescriptions[0]
            ? [{ remarks: "", description: fixedDescriptions[0] ?? "", amount: "", actual: "", accountCode: "", budgetLine: "" }]
            : []
      ).map((row) => ({
        remarks: row.remarks ?? "",
        description: row.description ?? "",
        amount: row.amount ?? "",
        actual: row.actual ?? "",
        accountCode: row.accountCode ?? "",
        budgetLine: row.budgetLine ?? "",
      }));

  const rowShape = (row) => ({
    remarks: row.remarks ?? "",
    description: row.description ?? "",
    amount: row.amount ?? "",
    actual: row.actual ?? "",
    accountCode: row.accountCode ?? "",
    budgetLine: row.budgetLine ?? "",
  });

  const updateFixed = (index, key, val) => {
    const next = [...fixedRows];
    if (!next[index])
      next[index] = { remarks: "", description: "", amount: "", actual: "", accountCode: "", budgetLine: "" };
    next[index] = { ...next[index], [key]: val };
    emitChange({ ...data, fixed: next.map(rowShape) });
  };

  const updateFixedBudget = (index, selectedValue) => {
    const opt = getBudgetOption(selectedValue);
    const next = [...fixedRows];
    if (!next[index])
      next[index] = { remarks: "", description: "", amount: "", actual: "", accountCode: "", budgetLine: "" };
    next[index] = {
      ...next[index],
      description: opt?.description ?? selectedValue ?? "",
      accountCode: opt?.accountCode ?? "",
      budgetLine: opt?.id != null ? String(opt.id) : "",
      amount: opt ? next[index]?.amount ?? "" : "",
    };
    emitChange({ ...data, fixed: next.map(rowShape) });
  };

  const updateOther = (index, key, val) => {
    const next = [...others];
    next[index] = { ...(next[index] || {}), [key]: val };
    emitChange({ ...data, others: next });
  };

  const updateOtherBudget = (index, selectedValue) => {
    const opt = getBudgetOption(selectedValue);
    const source = isPettyCash ? displayOthers : others;
    const next = [...source];
    const current = next[index] || {};
    next[index] = {
      ...current,
      description: opt?.description ?? selectedValue ?? "",
      accountCode: opt?.accountCode ?? "",
      budgetLine: opt?.id != null ? String(opt.id) : "",
      amount: opt ? current.amount ?? "" : "",
    };
    emitChange({ ...data, others: next });
  };

  const addOther = () => {
    const newRow = isPettyCash
      ? {
          remarks: "",
          description: "",
          amount: "",
          accountCode: "",
          budgetLine: "",
        }
      : {
          remarks: "",
          description: "",
          amount: "",
          actual: "",
          accountCode: "",
          budgetLine: "",
        };
    emitChange({
      ...data,
      others: [...(isPettyCash ? displayOthers : others), newRow],
    });
  };

  const removeOther = (index) => {
    const source = isPettyCash ? displayOthers : others;
    const next = source.filter((_, i) => i !== index);
    emitChange({ ...data, others: next });
  };

  const rowsToRender = isPettyCash ? displayOthers : others;
  const updateOtherIndex = (index, key, val) => {
    const source = isPettyCash ? displayOthers : others;
    const next = [...source];
    next[index] = { ...(next[index] || {}), [key]: val };
    emitChange({ ...data, others: next });
  };

  const amountTakenTotal = [...fixedRows, ...rowsToRender].reduce(
    (sum, row) =>
      sum + (parseFloat(String(row.amount || "0").replace(/,/g, "")) || 0),
    0,
  );

  const lpoRows = isLpo
    ? [...fixed, ...others].map((r) => {
        const shaped = lpoLineRowShape(r);
        const amt = lineTotalFromQtyPriceTax(
          shaped.quantity,
          shaped.unitPrice,
          shaped.taxRate,
          shaped.amount,
        );
        return { ...shaped, amount: amt || shaped.amount || "" };
      })
    : [];

  const setLpoRows = (next) => {
    emitChange({
      ...data,
      fixed: [],
      others: next.map((r) => lpoLineRowShape(r)),
    });
  };

  const updateLpoRow = (index, patch) => {
    const next = lpoRows.map((r, i) => {
      if (i !== index) return r;
      const merged = { ...r, ...patch };
      if ("quantity" in patch || "unitPrice" in patch || "taxRate" in patch) {
        merged.amount = lineTotalFromQtyPriceTax(
          merged.quantity,
          merged.unitPrice,
          merged.taxRate,
          merged.amount,
        );
      }
      return lpoLineRowShape(merged);
    });
    setLpoRows(next);
  };

  const lpoTotals = isLpo
    ? getLpoFinancialTotalsFromItems({ fixed: [], others: lpoRows })
    : { subtotalExcl: 0, tax: 0, totalIncl: 0 };

  if (isLpo) {
    return (
      <div className="min-w-0">
        <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-sm">
          <table className="w-full min-w-[42rem] border-collapse text-xs tabular-nums">
            <thead>
              <tr className="text-left">
                <th className={`${TH_CELL} w-10`}>Item #</th>
                <th
                  className={`${TH_CELL} min-w-[6rem] max-w-[11rem] w-[28%]`}
                >
                  Description of Good/Service
                </th>
                <th className={`${TH_CELL} w-20 text-right`}>Quantity</th>
                <th className={`${TH_CELL} w-24 text-right`}>
                  Unit Price{currencyHeaderSuffix}
                </th>
                <th className={`${TH_CELL} w-16 text-right`}>Tax %</th>
                <th className={`${TH_CELL} w-28 text-right`}>
                  Total Price{currencyHeaderSuffix}
                </th>
                <th
                  className={`${TH_CELL} w-9 px-0 text-center`}
                  aria-label="Remove"
                />
              </tr>
            </thead>
            <tbody>
              {lpoRows.map((row, index) => (
                <tr key={`lpo-${index}`} className="bg-surface">
                  <td className={TD_SN}>{index + 1}</td>
                  <td className={`${TD_CELL} min-w-[6rem] max-w-[11rem] w-[28%]`}>
                    <textarea
                      className={`${EXPENSE_TEXTAREA_CLASS} ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                      value={row.description ?? ""}
                      onChange={(e) =>
                        updateLpoRow(index, { description: e.target.value })
                      }
                      placeholder="Description"
                      rows={2}
                      aria-label={`Description for line ${index + 1}`}
                      disabled={disabled}
                      readOnly={disabled}
                    />
                  </td>
                  <td className={`${TD_CELL} w-20 text-right`}>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`${EXPENSE_INPUT_CLASS} text-right ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                      value={row.quantity ?? "1"}
                      onChange={(e) =>
                        updateLpoRow(index, {
                          quantity: stripCommas(e.target.value),
                        })
                      }
                      placeholder="1"
                      aria-label={`Quantity for line ${index + 1}`}
                      disabled={disabled}
                      readOnly={disabled}
                    />
                  </td>
                  <td className={`${TD_CELL} w-24 text-right`}>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`${EXPENSE_INPUT_CLASS} text-right ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                      value={
                        activeAmountField === `lpo-up-${index}`
                          ? row.unitPrice ?? ""
                          : formatAmountInput(row.unitPrice ?? "")
                      }
                      onFocus={() => setActiveAmountField(`lpo-up-${index}`)}
                      onChange={(e) =>
                        updateLpoRow(index, {
                          unitPrice: stripCommas(e.target.value),
                        })
                      }
                      onBlur={(e) => {
                        updateLpoRow(index, {
                          unitPrice: stripCommas(e.target.value),
                        });
                        setActiveAmountField(null);
                      }}
                      placeholder="0.00"
                      aria-label={`Unit price for line ${index + 1}`}
                      disabled={disabled}
                      readOnly={disabled}
                    />
                  </td>
                  <td className={`${TD_CELL} w-16 text-right`}>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`${EXPENSE_INPUT_CLASS} text-right ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                      value={row.taxRate ?? String(DEFAULT_LPO_TAX_RATE)}
                      onChange={(e) =>
                        updateLpoRow(index, {
                          taxRate: stripCommas(e.target.value),
                        })
                      }
                      placeholder={String(DEFAULT_LPO_TAX_RATE)}
                      aria-label={`Tax percent for line ${index + 1}`}
                      disabled={disabled}
                      readOnly={disabled}
                    />
                  </td>
                  <td
                    className={`${CELL} px-2 py-1.5 text-right text-xs font-medium tabular-nums text-text-primary`}
                  >
                    {currencyPrefix}
                    {formatAmountInput(row.amount ?? "") || "0.00"}
                  </td>
                  <td className={`${CELL} w-9 p-0 text-center`}>
                    {!disabled && lpoRows.length > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          setLpoRows(lpoRows.filter((_, i) => i !== index))
                        }
                        className="inline-flex rounded-sm p-1 text-text-muted transition hover:bg-danger-soft/50 hover:text-danger focus:outline-none focus:ring-2 focus:ring-primary/20"
                        aria-label="Remove row"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-t-border bg-surface-muted/90">
                <td
                  colSpan={5}
                  className={`${CELL} border-t-0 px-2 py-1.5 text-right text-[11px] font-medium text-text-secondary`}
                >
                  Subtotal (excl. VAT){currencyHeaderSuffix}
                </td>
                <td
                  className={`${CELL} border-t-0 px-2 py-1.5 text-right text-xs font-semibold tabular-nums text-text-primary`}
                >
                  {currencyPrefix}
                  {lpoTotals.subtotalExcl.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
                <td className={`${CELL} border-t-0`} aria-hidden />
              </tr>
              <tr className="bg-surface-muted/90">
                <td
                  colSpan={5}
                  className={`${CELL} border-t-0 px-2 py-1.5 text-right text-[11px] font-medium text-text-secondary`}
                >
                  VAT{currencyHeaderSuffix}
                </td>
                <td
                  className={`${CELL} border-t-0 px-2 py-1.5 text-right text-xs font-semibold tabular-nums text-text-primary`}
                >
                  {currencyPrefix}
                  {lpoTotals.tax.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
                <td className={`${CELL} border-t-0`} aria-hidden />
              </tr>
              <tr className="border-t-2 border-t-primary/20 bg-surface-muted/90">
                <td
                  colSpan={5}
                  className={`${CELL} border-t-0 px-2 py-1.5 text-right text-[11px] font-medium text-text-secondary`}
                >
                  Total (incl. VAT){currencyHeaderSuffix}
                </td>
                <td
                  className={`${CELL} border-t-0 px-2 py-1.5 text-right text-xs font-semibold tabular-nums text-text-primary`}
                >
                  {currencyPrefix}
                  {lpoTotals.totalIncl.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
                <td className={`${CELL} border-t-0`} aria-hidden />
              </tr>
            </tbody>
          </table>
        </div>
        {!disabled && (
          <div className="mt-1.5">
            <button
              type="button"
              onClick={() =>
                setLpoRows([
                  ...lpoRows,
                  lpoLineRowShape({
                    description: "",
                    quantity: "1",
                    unitPrice: "",
                    taxRate: String(DEFAULT_LPO_TAX_RATE),
                    amount: "",
                  }),
                ])
              }
              className="inline-flex items-center gap-1 rounded-lg border border-dashed border-border bg-surface px-2 py-1 text-[11px] font-medium text-text-secondary transition hover:border-primary/30 hover:bg-surface-muted hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <Plus className="size-3.5" />
              Add line item
            </button>
          </div>
        )}
        {error && <p className="mt-1 text-xs text-danger">{error}</p>}
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-sm">
        <table className="w-full min-w-[32rem] border-collapse text-xs tabular-nums">
          <thead>
            <tr className="text-left">
              <th className={`${TH_CELL} w-10`}>#</th>
              <th className={`${TH_CELL} min-w-[12rem] w-72 max-w-[18rem]`}>
                Description
              </th>
              <th className={`${TH_CELL} w-48 max-w-52`}>Budget</th>
              <th className={`${TH_CELL} w-36 max-w-36`}>
                Amount{currencyHeaderSuffix}
              </th>
              {!isPettyCash && isRetirement && (
                <th className={`${TH_CELL} w-28`}>
                  Actual{currencyHeaderSuffix}
                </th>
              )}
              <th className={`${TH_CELL} w-32 max-w-32`}>Account code</th>
              {!isPettyCash && rowsToRender.length > 0 && (
                <th className={`${TH_CELL} w-9 px-0 text-center`} aria-label="Remove" />
              )}
              {isPettyCash && (
                <th className={`${TH_CELL} w-9 px-0 text-center`} aria-label="Remove" />
              )}
            </tr>
          </thead>
          <tbody>
            {!isPettyCash &&
              fixedRows.map((row, index) => (
                <tr key={`fixed-${index}`} className="bg-surface">
                  <td className={TD_SN}>{index + 1}</td>
                  <td className={`${TD_CELL} min-w-[12rem] w-72 max-w-[18rem]`}>
                    <textarea
                      className={`${EXPENSE_TEXTAREA_CLASS} ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                      value={row.remarks ?? ""}
                      onChange={(e) =>
                        updateFixed(index, "remarks", e.target.value)
                      }
                      placeholder="Description"
                      rows={2}
                      aria-label={`Description for row ${index + 1}`}
                      disabled={disabled}
                      readOnly={disabled}
                    />
                  </td>
                  <td className={`${TD_CELL} w-48 max-w-52`}>
                    <div className="flex min-w-0 flex-col gap-0">
                      <select
                        className={`${EXPENSE_INPUT_CLASS} cursor-pointer ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                        value={row.budgetLine || row.description || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (budgetOptions?.length) {
                            updateFixedBudget(index, val);
                          } else {
                            updateFixed(index, "description", val);
                          }
                        }}
                        aria-label={`Budget for row ${index + 1}`}
                        disabled={disabled}
                        title={
                          budgetOptions?.length && (row.budgetLine || row.description)
                            ? (() => {
                                const opt = getBudgetOption(
                                  row.budgetLine ?? row.description,
                                );
                                const t = getBudgetSelectOptionTitle(opt);
                                return t || undefined;
                              })()
                            : undefined
                        }
                      >
                        <option value="">Select budget</option>
                        {budgetOptions?.length
                          ? budgetOptions.map((opt) => {
                              const label = getBudgetSelectOptionLabel(opt);
                              const optTitle = getBudgetSelectOptionTitle(opt);
                              return (
                                <option
                                  key={opt.id ?? opt.description}
                                  value={opt.id ?? opt.description}
                                  title={optTitle}
                                >
                                  {label}
                                </option>
                              );
                            })
                          : getDescriptionOptions(row.description ?? "").map((description) => (
                              <option key={description} value={description} title={description}>
                                {description}
                              </option>
                            ))}
                      </select>
                      {budgetOptions?.length > 0 && (row.budgetLine || row.description) && (() => {
                        const opt = getBudgetOption(row.budgetLine ?? row.description);
                        if (!opt) return null;
                        const alloc = opt.allocatedAmount;
                        const avail = opt.availableAmount;
                        if (alloc == null && avail == null) return null;
                        return (
                          <p className="border-t border-border px-2 py-0.5 text-[10px] leading-tight text-text-muted">
                            {alloc != null && (
                              <span>Alloc {Number(alloc).toLocaleString()}</span>
                            )}
                            {alloc != null && avail != null && " · "}
                            {avail != null && (
                              <span className="font-medium text-success">
                                Avl {Number(avail).toLocaleString()}
                              </span>
                            )}
                          </p>
                        );
                      })()}
                    </div>
                  </td>
                  <td className={`${TD_CELL} w-36 max-w-36`}>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`${EXPENSE_INPUT_CLASS} ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                      value={
                        activeAmountField === `fixed-${index}`
                          ? row.amount ?? ""
                          : formatAmountInput(row.amount ?? "")
                      }
                      onFocus={() => setActiveAmountField(`fixed-${index}`)}
                      onChange={(e) =>
                        updateFixed(index, "amount", stripCommas(e.target.value))
                      }
                      onBlur={(e) => {
                        updateFixed(index, "amount", stripCommas(e.target.value));
                        setActiveAmountField(null);
                      }}
                      placeholder="Enter amount (e.g. 1,000.00)"
                      aria-label={`Amount for row ${index + 1}`}
                      disabled={disabled || !!(budgetOptions?.length && !row.budgetLine && !row.description)}
                    />
                  </td>
                  {isRetirement && (
                    <td className={TD_CELL}>
                      <input
                        type="text"
                        inputMode="decimal"
                        className={`${EXPENSE_INPUT_CLASS} ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                        value={
                          activeActualField === `fixed-actual-${index}`
                            ? row.actual ?? ""
                            : formatAmountInput(row.actual ?? "")
                        }
                        onFocus={() => setActiveActualField(`fixed-actual-${index}`)}
                        onChange={(e) =>
                          updateFixed(index, "actual", stripCommas(e.target.value))
                        }
                        onBlur={(e) => {
                          updateFixed(index, "actual", stripCommas(e.target.value));
                          setActiveActualField(null);
                        }}
                        placeholder="0.00"
                        aria-label={`Actual for row ${index + 1}`}
                        disabled={disabled}
                        readOnly={disabled}
                      />
                    </td>
                  )}
                  <td className={`${TD_CELL} w-32 max-w-32`}>
                    <div className="min-w-0">
                      <input
                        type="text"
                        className={`${EXPENSE_INPUT_CLASS} ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                        value={row.accountCode ?? ""}
                        readOnly
                        placeholder={budgetOptions?.length && !row.budgetLine && !row.description ? "Select budget" : ""}
                        aria-label={`Account code for row ${index + 1}`}
                        disabled={disabled}
                      />
                    </div>
                  </td>
                  {others.length > 0 && (
                    <td className={`${CELL} w-9 p-0`} aria-hidden />
                  )}
                </tr>
              ))}
            {(isPettyCash ? displayOthers : others).map((row, index) => (
              <tr
                key={`other-${index}`}
                className="bg-primary/[0.035]"
              >
                <td className={TD_SN}>
                  {isPettyCash ? index + 1 : fixedRows.length + index + 1}
                </td>
                <td className={`${TD_CELL} min-w-[12rem] w-72 max-w-[18rem]`}>
                  <textarea
                    className={`${EXPENSE_TEXTAREA_CLASS} ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                    value={row.remarks ?? ""}
                    onChange={(e) =>
                      isPettyCash
                        ? updateOtherIndex(index, "remarks", e.target.value)
                        : updateOther(index, "remarks", e.target.value)
                    }
                    placeholder="Description"
                    rows={2}
                    aria-label="Description"
                    disabled={disabled}
                    readOnly={disabled}
                  />
                </td>
                <td className={`${TD_CELL} w-48 max-w-52`}>
                  {isPettyCash && !budgetOptions?.length ? (
                    <input
                      type="text"
                      className={`${EXPENSE_INPUT_CLASS} ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                      value={row.description ?? ""}
                      onChange={(e) =>
                        updateOtherIndex(index, "description", e.target.value)
                      }
                      placeholder="Budget"
                      aria-label="Budget"
                      disabled={disabled}
                      readOnly={disabled}
                    />
                  ) : (
                    <div className="flex min-w-0 flex-col gap-0">
                      <select
                        className={`${EXPENSE_INPUT_CLASS} cursor-pointer ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                        value={row.budgetLine || row.description || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (budgetOptions?.length) {
                            updateOtherBudget(index, val);
                          } else {
                            updateOther(index, "description", val);
                          }
                        }}
                        aria-label="Budget"
                        disabled={disabled}
                        title={
                          budgetOptions?.length && (row.budgetLine || row.description)
                            ? (() => {
                                const opt = getBudgetOption(
                                  row.budgetLine ?? row.description,
                                );
                                const t = getBudgetSelectOptionTitle(opt);
                                return t || undefined;
                              })()
                            : undefined
                        }
                      >
                        <option value="">Select budget</option>
                        {budgetOptions?.length
                          ? budgetOptions.map((opt) => {
                              const label = getBudgetSelectOptionLabel(opt);
                              const optTitle = getBudgetSelectOptionTitle(opt);
                              return (
                                <option
                                  key={opt.id ?? opt.description}
                                  value={opt.id ?? opt.description}
                                  title={optTitle}
                                >
                                  {label}
                                </option>
                              );
                            })
                          : getDescriptionOptions(row.description ?? "").map((description) => (
                              <option key={description} value={description} title={description}>
                                {description}
                              </option>
                            ))}
                      </select>
                      {budgetOptions?.length > 0 && (row.budgetLine || row.description) && (() => {
                        const opt = getBudgetOption(row.budgetLine ?? row.description);
                        if (!opt) return null;
                        const alloc = opt.allocatedAmount;
                        const avail = opt.availableAmount;
                        if (alloc == null && avail == null) return null;
                        return (
                          <p className="border-t border-border px-2 py-0.5 text-[10px] leading-tight text-text-muted">
                            {alloc != null && (
                              <span>Alloc {Number(alloc).toLocaleString()}</span>
                            )}
                            {alloc != null && avail != null && " · "}
                            {avail != null && (
                              <span className="font-medium text-success">
                                Avl {Number(avail).toLocaleString()}
                              </span>
                            )}
                          </p>
                        );
                      })()}
                    </div>
                  )}
                </td>
                <td className={`${TD_CELL} w-36 max-w-36`}>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`${EXPENSE_INPUT_CLASS} ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                    value={
                      activeAmountField === `other-${index}`
                        ? row.amount ?? ""
                        : formatAmountInput(row.amount ?? "")
                    }
                    onFocus={() => setActiveAmountField(`other-${index}`)}
                    onChange={(e) =>
                      isPettyCash
                        ? updateOtherIndex(index, "amount", stripCommas(e.target.value))
                        : updateOther(index, "amount", stripCommas(e.target.value))
                    }
                    onBlur={(e) => {
                      const normalized = stripCommas(e.target.value);
                      if (isPettyCash) {
                        updateOtherIndex(index, "amount", normalized);
                      } else {
                        updateOther(index, "amount", normalized);
                      }
                      setActiveAmountField(null);
                    }}
                    placeholder="Enter amount (e.g. 1,000.00)"
                    aria-label="Amount"
                    disabled={disabled || !!(budgetOptions?.length && !row.budgetLine && !row.description)}
                  />
                </td>
                {!isPettyCash && isRetirement && (
                  <td className={TD_CELL}>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`${EXPENSE_INPUT_CLASS} ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                      value={
                        activeActualField === `other-actual-${index}`
                          ? row.actual ?? ""
                          : formatAmountInput(row.actual ?? "")
                      }
                      onFocus={() => setActiveActualField(`other-actual-${index}`)}
                      onChange={(e) =>
                        updateOther(index, "actual", stripCommas(e.target.value))
                      }
                      onBlur={(e) => {
                        updateOther(index, "actual", stripCommas(e.target.value));
                        setActiveActualField(null);
                      }}
                      placeholder="0.00"
                      aria-label="Actual"
                      disabled={disabled}
                      readOnly={disabled}
                    />
                  </td>
                )}
                <td className={`${TD_CELL} w-32 max-w-32`}>
                  <div className="min-w-0">
                    <input
                      type="text"
                      className={`${EXPENSE_INPUT_CLASS} ${disabled ? DISABLED_INPUT_CLASS : ""}`}
                      value={row.accountCode ?? ""}
                      readOnly
                      placeholder={budgetOptions?.length && !row.budgetLine && !row.description ? "Select budget" : ""}
                      aria-label="Account code"
                      disabled={disabled}
                    />
                  </div>
                </td>
                <td className={`${CELL} w-9 p-0 text-center`}>
                  {!disabled && !(budgetOptions?.length && others.length <= 1) && (
                    <button
                      type="button"
                      onClick={() => removeOther(index)}
                      className="inline-flex rounded-sm p-1 text-text-muted transition hover:bg-danger-soft/50 hover:text-danger focus:outline-none focus:ring-2 focus:ring-primary/20"
                      aria-label="Remove row"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-t-primary/20 bg-surface-muted/90">
              <td
                colSpan={3}
                className={`${CELL} border-t-0 px-2 py-1.5 text-[11px] font-medium text-text-secondary`}
              >
                Total{currencyHeaderSuffix}
              </td>
              <td
                className={`${CELL} border-t-0 px-2 py-1.5 text-right text-xs font-semibold tabular-nums text-text-primary`}
              >
                {currencyPrefix}
                {amountTakenTotal.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
              {isRetirement && !isPettyCash && (
                <td
                  className={`${CELL} border-t-0 bg-surface-muted/90`}
                  aria-hidden
                />
              )}
              <td
                className={`${CELL} border-t-0 bg-surface-muted/90`}
                aria-hidden
              />
              {(isPettyCash || rowsToRender.length > 0) && (
                <td
                  className={`${CELL} border-t-0 bg-surface-muted/90`}
                  aria-hidden
                />
              )}
            </tr>
          </tbody>
        </table>
      </div>
      {!disabled && (
        <div className="mt-1.5">
          <button
            type="button"
            onClick={addOther}
            className="inline-flex items-center gap-1 rounded-lg border border-dashed border-border bg-surface px-2 py-1 text-[11px] font-medium text-text-secondary transition hover:border-primary/30 hover:bg-surface-muted hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <Plus className="size-3.5" />
            {isPettyCash ? "Add row" : "Add other item"}
          </button>
        </div>
      )}
      {!isPettyCash && (
        <p className="mt-1 text-[11px] leading-snug text-text-muted">
          Add rows below for items not in the list above.
        </p>
      )}
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
};

export default ExpenseTable;
